import { Link } from 'react-router-dom';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const CHALLENGES = [
  {
    icon: 'ri-close-line',
    title: 'Multiplication Challenge',
    color: 'bg-green-100 text-green-700',
    border: 'border-green-200',
    desc: 'Test your multiplication speed from basic single-digit products to expert-level word problems set in a tennis context.',
    levels: [
      { label: 'Easy', example: '3 × 7 = ?' },
      { label: 'Medium', example: '8 × 9 = ?' },
      { label: 'Hard', example: '15 × 17 = ?' },
      { label: 'Expert', example: 'A player hits 23 shots/game over 14 games. Total?' },
    ],
    path: '/multiplication-challenge',
  },
  {
    icon: 'ri-box-3-line',
    title: 'Volume Challenge',
    color: 'bg-teal-100 text-teal-700',
    border: 'border-teal-200',
    desc: 'Calculate volumes of 3D shapes. Begins with simple boxes and escalates to spheres, cones, and composite solid figures.',
    levels: [
      { label: 'Easy', example: 'Volume of a 4×5×3 cm box?' },
      { label: 'Medium', example: 'Cylinder r=3, h=7 cm — V=?' },
      { label: 'Hard', example: 'Cone r=4, h=9 — V=?' },
      { label: 'Expert', example: 'Box with cylindrical hole — net V=?' },
    ],
    path: '/volume-challenge',
  },
  {
    icon: 'ri-divide-line',
    title: 'Division Challenge',
    color: 'bg-emerald-100 text-emerald-700',
    border: 'border-emerald-200',
    desc: 'Divide, conquer, and score. From whole-number quotients to remainders, decimals, and tennis-themed story problems.',
    levels: [
      { label: 'Easy', example: '48 ÷ 6 = ?' },
      { label: 'Medium', example: '73 ÷ 8 = ? (with remainder)' },
      { label: 'Hard', example: '456 ÷ 13 = ? (1 decimal)' },
      { label: 'Expert', example: '340 balls shared by 15 players?' },
    ],
    path: '/division-challenge',
  },
];

const FEATURES = [
  { icon: 'ri-tennis-ball-fill', title: 'Real Tennis Scoring', desc: 'Points, games, sets, and matches all follow official tennis scoring rules — including Deuce and Advantage.' },
  { icon: 'ri-cpu-line', title: 'Dynamic CPU Opponent', desc: 'Play against a simulated CPU that wins every point you miss. Every rally counts.' },
  { icon: 'ri-time-line', title: 'Timed Rounds (Hard/Expert)', desc: 'Hard and Expert levels include a countdown timer per question — answer fast or the CPU takes the point.' },
  { icon: 'ri-infinity-line', title: 'Infinite Questions', desc: 'Questions are algorithmically generated each round — no two matches are the same.' },
  { icon: 'ri-bar-chart-2-line', title: 'Progressive Difficulty', desc: 'Four carefully calibrated difficulty tiers ensure there\'s always a new challenge waiting for you.' },
  { icon: 'ri-refresh-line', title: 'Instant Replay', desc: 'Lost the match? Restart instantly and try again. Your skills improve with every set.' },
];

export default function GameOverview() {
  return (
    <div className="bg-white min-h-screen">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-16 px-6 bg-green-900 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <img
            src="https://public.readdy.ai/ai/img_res/0e3bc7fc47b027054d74aa9fabc65b62.jpg"
            alt=""
            className="w-full h-full object-cover object-top"
          />
        </div>
        <div className="relative max-w-3xl mx-auto text-center">
          <span className="text-xs font-bold uppercase tracking-widest text-lime-400 bg-lime-400/10 border border-lime-400/30 px-3 py-1 rounded-full">
            Game Overview
          </span>
          <h1
            className="text-5xl font-bold mt-4 mb-3"
            style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}
          >
            HOW THE GAME WORKS
          </h1>
          <p className="text-green-300 text-lg max-w-xl mx-auto">
            Math Tennis is a question-per-rally game where your answers directly determine the score on a real tennis court scoreboard.
          </p>
        </div>
      </section>

      {/* Core mechanic */}
      <section className="py-16 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="bg-green-50 rounded-2xl p-8 border border-green-100 mb-10">
            <h2 className="text-2xl font-bold text-green-900 mb-4" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}>
              THE CORE MECHANIC
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 text-sm text-gray-700 leading-relaxed">
              <div>
                <p className="mb-3">
                  Each round presents a single math question with four multiple-choice answers. You have one chance to pick correctly.
                </p>
                <p className="mb-3">
                  <strong className="text-green-800">Correct answer →</strong> You win the point. Your tennis score advances: 0 → 15 → 30 → 40 → Game.
                </p>
                <p>
                  <strong className="text-red-700">Wrong answer →</strong> The CPU wins the point and their score advances instead.
                </p>
              </div>
              <div>
                <p className="mb-3">
                  Win enough games to win the set. Win 2 sets to win the match. Standard tennis rules apply — including <strong>Deuce</strong> (when both reach 40) and <strong>Advantage</strong>.
                </p>
                <p className="font-medium text-green-800">
                  Best of 3 sets per match. First player to 2 sets wins!
                </p>
              </div>
            </div>
          </div>

          {/* Challenge types */}
          <h2 className="text-3xl font-bold text-green-900 mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}>
            CHALLENGE TYPES
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            {CHALLENGES.map(ch => (
              <div key={ch.title} className={`rounded-xl border ${ch.border} p-5`}>
                <div className={`w-10 h-10 flex items-center justify-center rounded-full ${ch.color} mb-3`}>
                  <i className={`${ch.icon} text-xl`} />
                </div>
                <h3 className="font-bold text-green-900 mb-2">{ch.title}</h3>
                <p className="text-sm text-gray-500 mb-4">{ch.desc}</p>
                <div className="space-y-2 mb-4">
                  {ch.levels.map(l => (
                    <div key={l.label} className="flex gap-2 text-xs">
                      <span className="font-semibold text-green-700 w-14 flex-shrink-0">{l.label}:</span>
                      <span className="text-gray-500 italic">"{l.example}"</span>
                    </div>
                  ))}
                </div>
                <Link
                  to={ch.path}
                  className="flex items-center justify-center gap-1.5 w-full py-2 rounded-lg bg-green-800 text-white text-sm font-semibold hover:bg-green-700 cursor-pointer whitespace-nowrap"
                >
                  Play <i className="ri-arrow-right-line" />
                </Link>
              </div>
            ))}
          </div>

          {/* Features grid */}
          <h2 className="text-3xl font-bold text-green-900 mb-6" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}>
            GAME FEATURES
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {FEATURES.map(f => (
              <div key={f.title} className="flex gap-4 p-4 rounded-xl bg-gray-50 border border-gray-100">
                <div className="w-10 h-10 flex items-center justify-center bg-green-100 rounded-lg flex-shrink-0">
                  <i className={`${f.icon} text-green-700 text-lg`} />
                </div>
                <div>
                  <h4 className="font-semibold text-green-900 text-sm mb-1">{f.title}</h4>
                  <p className="text-xs text-gray-500 leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 px-6 bg-green-900 text-white text-center">
        <h2 className="text-4xl font-bold mb-3" style={{ fontFamily: "'Bebas Neue', sans-serif", letterSpacing: '0.04em' }}>
          READY TO PLAY?
        </h2>
        <p className="text-green-300 mb-6">Choose your level and start your match right now.</p>
        <Link
          to="/level-selector"
          className="inline-block px-8 py-3.5 rounded-full bg-lime-400 text-green-900 font-bold hover:bg-lime-300 cursor-pointer transition-all whitespace-nowrap"
        >
          Select Level &amp; Play
        </Link>
      </section>

      <Footer />
    </div>
  );
}
